import React from 'react';
import { Box, Container, Divider, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 5, md: 8 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="md">
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
            A3 Research and A3 High School
          </Typography>
          <Typography variant="h3" component="h1" sx={{ mt: 1, mb: 2, fontWeight: 800 }}>
            Privacy Policy
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Effective date: September 13, 2026
          </Typography>

          <Stack spacing={3} divider={<Divider flexItem />}>
            <Box>
              <Typography variant="h6" component="h2" gutterBottom>1. Overview</Typography>
              <Typography color="text.secondary">
                This Privacy Policy explains how A3 Research and A3 High School (collectively, “A3”, “we”, “us”, or “our”) collect, use, disclose, and protect information when you use our websites, learning tools, mock examinations, school services, and related applications (the “Services”).
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>2. Information We Collect</Typography>
              <Typography color="text.secondary" paragraph>
                We collect information you provide directly, such as your name, email address, password, school details, educational level, department, location, parent or guardian contact details, support messages, and information submitted through learning, quiz, assignment, laboratory, or mock-test features.
              </Typography>
              <Typography color="text.secondary" paragraph>
                We also receive account and activity information generated when you use the Services, including login records, progress, selected subjects, assessment responses, results, device and browser information, IP address, security events, and diagnostic or usage data.
              </Typography>
              <Typography color="text.secondary">
                Where you sign in through a third-party provider, we may receive the profile information that provider makes available to us, subject to your provider settings.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>3. How We Use Information</Typography>
              <Typography color="text.secondary">
                We use information to provide, personalize, secure, and improve the Services; create and maintain accounts; deliver subject content and assessments; administer school and parent features; process payments; communicate about accounts and service updates; respond to support requests; detect abuse, fraud, and examination violations; maintain records; and comply with legal obligations.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>4. Communications</Typography>
              <Typography color="text.secondary">
                We may send transactional communications about registration, account security, password recovery, payments, support, and service operations. Where configured and permitted, registration or operational alerts may be sent to an administrator through email, WhatsApp, or another approved channel. We do not use your information to send marketing communications without an appropriate legal basis or your consent where required.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>5. Sharing Information</Typography>
              <Typography color="text.secondary">
                We may share information with schools, parents or guardians where the relevant account relationship and permissions allow; service providers that host, secure, operate, or support the Services; payment, authentication, communication, analytics, and infrastructure providers; professional advisers; and public authorities when required by law or necessary to protect rights, safety, and the integrity of examinations. We do not sell personal information.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>6. Cookies and Local Storage</Typography>
              <Typography color="text.secondary">
                The Services may use cookies, local storage, session storage, and similar technologies to keep you signed in, remember preferences, provide secure exam functionality, maintain user experience, and understand service performance. You can manage browser storage controls, but disabling them may affect features that require authentication or saved preferences.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>7. Data Retention and Security</Typography>
              <Typography color="text.secondary">
                We retain information for as long as reasonably necessary for the purposes described in this Policy, including account administration, educational records, security, dispute resolution, and legal or regulatory requirements. We use reasonable administrative, technical, and organizational safeguards, but no internet transmission or storage system can be guaranteed completely secure.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>8. Children and Students</Typography>
              <Typography color="text.secondary">
                The Services may be used by students under the supervision and responsibility of their school, parent, or guardian. Where required by applicable law, a parent, guardian, or school must provide or authorize the information submitted by a student. If you believe a child has provided personal information improperly, contact us so we can review and take appropriate action.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>9. Your Choices and Rights</Typography>
              <Typography color="text.secondary">
                Depending on applicable law, you may have rights to access, correct, export, restrict, object to, or request deletion of personal information. Some records may need to be retained for legal, security, accounting, educational, or examination-integrity reasons. To make a privacy request, contact us using the details below. We may need to verify your identity before completing a request.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>10. Third-Party Services and Links</Typography>
              <Typography color="text.secondary">
                The Services may contain links to third-party websites or use third-party services. Their privacy practices are governed by their own policies, and this Policy does not control information collected directly by those third parties.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>11. International Processing</Typography>
              <Typography color="text.secondary">
                Our service providers or infrastructure may process information in countries other than the country where you live. Where required, we take appropriate steps to protect information transferred across borders.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>12. Changes to This Policy</Typography>
              <Typography color="text.secondary">
                We may update this Policy when the Services, applicable law, or our privacy practices change. The revised version will be posted on this page with a new effective date. Your continued use of the Services after an update means the revised Policy applies to future use.
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" component="h2" gutterBottom>13. Contact Us</Typography>
              <Typography color="text.secondary">
                For privacy questions or requests, contact A3 Research at{' '}
                <Link href="mailto:contact@alchemistresearch.com">contact@alchemistresearch.com</Link>.
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 5 }}>
            <Link component={RouterLink} to="/" underline="hover">
              Return to home
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
